import React, { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import YnsIcon from '~/components/icons/YnsIcon'
import { useCandidate } from '../CandidateProvider'
import { useCustomActionPlan } from './CustomActionPlanProvider'
import { AddTaskModal } from './AddTaskModal'
import ResourceTile from './ResourceTile'
import { apiPatch } from '~/util/api'
import { Loading } from '~/lib/ui/loading'

const getSortByOptions = (trackerSectionInfo, totalSteps) => {
  if (!Array.isArray(trackerSectionInfo)) return []
  const options = trackerSectionInfo
    .map((sectionObj) => Object.keys(sectionObj)[0])
    .filter(
      (key) =>
        (key &&
          (key.startsWith('day_') ||
            key.startsWith('week_') ||
            key.startsWith('month_'))) ||
        key.startsWith('months_'),
    )
    .map((key) => {
      const sectionObj = totalSteps.find((s) => s[key])
      const subs = sectionObj?.[key]?.subsections || []
      const allDone =
        subs.length > 0 &&
        subs.every((sub) => sub.completed || sub.skipped || sub.coach_skipped)
      let label
      if (key.startsWith('months_')) {
        const parts = key.split('_')
        if (parts.length === 3) {
          label = `Months ${parts[1]}-${parts[2]}`
        } else {
          label = key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
        }
      } else {
        label = key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
      }
      return {
        value: key,
        label: label + (allDone ? ' (Completed)' : ''),
      }
    })
  return [{ value: 'coach_added', label: 'Coach Added Tasks' }, ...options]
}

const getFirstIncompleteSection = (totalSteps) => {
  for (const sectionObj of totalSteps) {
    const key = Object.keys(sectionObj)[0]
    const subs = sectionObj[key]?.subsections || []
    if (
      subs.some((sub) => !sub.completed && !sub.skipped && !sub.coach_skipped)
    ) {
      return key
    }
  }
  // fallback: first section
  if (totalSteps.length > 0) return Object.keys(totalSteps[0])[0]
  return 'day_1'
}

const getTasksForSection = (totalSteps, sectionKey) => {
  const sectionObj = totalSteps.find((s) => s[sectionKey])
  if (!sectionObj || !sectionObj[sectionKey]?.subsections) return []
  return sectionObj[sectionKey].subsections.map((sub, idx) => {
    let status
    if (sub.completed) {
      status = 'Completed'
    } else if (sub.skipped) {
      status = 'Candidate Skipped'
    } else if (sub.coach_skipped) {
      status = 'Coach Skipped'
    }
    return {
      title: sub.name,
      status,
      description: sub.description,
      time: sub.length,
      type: sub.type,
      completed: sub.completed,
      skipped_by_candidate: sub.skipped,
      skipped_by_coach: sub.coach_skipped,
      added_by_coach: sub.coach_added || sub.slug.includes('coach_resource_'),
      idx,
    }
  })
}

const SkipButton = ({ onClick, skippedByCandidate, skippedByCoach }) => {
  const text = skippedByCandidate || skippedByCoach ? 'Unskip' : 'Skip'
  const iconName = skippedByCandidate || skippedByCoach ? 'return' : 'trash'
  const hoverText =
    skippedByCandidate || skippedByCoach
      ? 'group-hover:text-intoo-green-coachcta'
      : 'group-hover:text-[#D82727]'
  const hoverBg =
    skippedByCandidate || skippedByCoach
      ? 'hover:bg-willow-brook-100'
      : 'hover:bg-[#D827271A]'
  return (
    <button
      className={`group mr-6 flex flex-col items-center justify-center gap-2 rounded-[10px] p-2 ${hoverBg}`}
      onClick={onClick}
    >
      <YnsIcon name={iconName} className={`h-6 w-6 ${hoverText}`} />
      <span className={hoverText}>{text}</span>
    </button>
  )
}

const SortBy = ({ selectedSection, setSelectedSection, sortByOptions }) => (
  <div className="ml-auto flex items-center">
    <label htmlFor="sort-tasks" className="!mb-0 mr-2 !font-normal">
      Sort By:
    </label>
    <select
      id="sort-tasks"
      name="sort-tasks"
      className="min-w-48 rounded-md border border-gray-300 px-3 py-2"
      value={selectedSection}
      onChange={(e) => setSelectedSection(e.target.value)}
    >
      {sortByOptions.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
)

const CustomizeActionPlanSection = () => {
  const { candidateName } = useCandidate()
  const { trackerSectionInfo, goal, timeline } = useCustomActionPlan()
  const [totalSteps, setTotalSteps] = useState([])
  const [selectedSection, setSelectedSection] = useState('day_1')

  useEffect(() => {
    if (trackerSectionInfo?.length > 0) {
      const deepCopy = JSON.parse(JSON.stringify([...trackerSectionInfo]))
      setTotalSteps(deepCopy)
      const firstIncomplete = getFirstIncompleteSection(deepCopy)
      setSelectedSection(firstIncomplete)
    }
  }, [trackerSectionInfo])

  let tasks = []
  if (selectedSection === 'coach_added') {
    if (totalSteps.length > 0) {
      const firstIncompleteKey = getFirstIncompleteSection(totalSteps)
      const firstSectionObj = totalSteps.find((s) => s[firstIncompleteKey])
      const sectionTasks = getTasksForSection(
        [firstSectionObj],
        firstIncompleteKey,
      )
      tasks = sectionTasks.filter((task) => task.added_by_coach)
    } else {
      tasks = []
    }
  } else {
    tasks = getTasksForSection(totalSteps, selectedSection)
  }

  const sortByOptions = getSortByOptions(totalSteps, totalSteps)

  const candidateId = location.pathname.split('/').pop()

  const coachSkipMutation = useMutation({
    mutationFn: async ({ _taskIdx, slug, coach_skipped }) => {
      const url = `/coach/candidates/${candidateId}/trackers/skip_item?slug=${slug}&coach_skipped=${coach_skipped}`
      return apiPatch(url, {})
    },
    onSuccess: (data, variables) => {
      if (data?.status === 200) {
        setTotalSteps((prevSteps) => {
          const stepsCopy = JSON.parse(JSON.stringify(prevSteps))
          const sectionObj = stepsCopy.find((s) => s[selectedSection])
          if (
            sectionObj &&
            sectionObj[selectedSection]?.subsections?.[variables.taskIdx]
          ) {
            sectionObj[selectedSection].subsections[
              variables.taskIdx
            ].coach_skipped = variables.coach_skipped
          }
          return stepsCopy
        })
      }
    },
    onError: (error) => {
      console.error('Failed to skip/unskip task', error)
    },
  })

  const candidateSkipMutation = useMutation({
    mutationFn: async ({ _taskIdx, slug, _skipped }) => {
      const url = `/coach/candidates/${candidateId}/trackers/skip_item?slug=${slug}`
      return apiPatch(url, {})
    },
    onSuccess: (data, variables) => {
      if (data?.status === 200) {
        setTotalSteps((prevSteps) => {
          const stepsCopy = JSON.parse(JSON.stringify(prevSteps))
          const sectionObj = stepsCopy.find((s) => s[selectedSection])
          if (
            sectionObj &&
            sectionObj[selectedSection]?.subsections?.[variables.taskIdx]
          ) {
            sectionObj[selectedSection].subsections[variables.taskIdx].skipped =
              variables.skipped
          }
          return stepsCopy
        })
      }
    },
    onError: (error) => {
      console.error('Failed to skip/unskip candidate task', error)
    },
  })

  const handleCoachSkip = (taskIdx) => {
    const sectionObj = totalSteps.find((s) => s[selectedSection])
    const task = sectionObj?.[selectedSection]?.subsections?.[taskIdx]
    if (!task?.slug) {
      console.error('Task slug not found')
      return
    }
    coachSkipMutation.mutate({
      taskIdx,
      slug: task.slug,
      coach_skipped: !task.coach_skipped,
    })
  }

  const handleCandidateSkip = (taskIdx) => {
    const sectionObj = totalSteps.find((s) => s[selectedSection])
    const task = sectionObj?.[selectedSection]?.subsections?.[taskIdx]
    if (!task?.slug) {
      console.error('Task slug not found')
      return
    }
    candidateSkipMutation.mutate({
      taskIdx,
      slug: task.slug,
      skipped: !task.skipped,
    })
  }

  return (
    <div className="rounded-[10px] border border-[#B9B9B9] p-6">
      <div>
        <h2 className="mb-8 font-roboto-slab text-[20px] font-normal leading-[100%] tracking-[0.12%] text-intoo-blue-medium">
          Candidate Information
        </h2>
        <div className="mb-6 flex flex-col gap-3">
          <h3 className="font-roboto text-[18px] font-normal leading-[100%] tracking-[0.1%]">
            {candidateName}
          </h3>
          <p>
            <span className="font-bold">
              {selectedSection
                .replace('_', ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())}
            </span>{' '}
            - {tasks.filter((t) => t.status === 'Completed').length} out of{' '}
            {tasks.length} tasks completed
          </p>
          <p>
            <span className="font-bold">User Goal</span> -{' '}
            <span className="capitalize">{goal || 'N/A'}</span>
          </p>
          <p>
            <span className="font-bold">Timeline</span> -{' '}
            <span className="capitalize">
              {timeline
                ? `${timeline} ${timeline === 1 ? 'Month' : 'Months'}`
                : '5 Months'}
            </span>
          </p>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <h2 className="tracking-[0.12% ] font-roboto-slab text-[20px] font-normal leading-[100%] text-intoo-blue-medium">
            Action Plan Tasks
          </h2>
          <SortBy
            selectedSection={selectedSection}
            setSelectedSection={setSelectedSection}
            sortByOptions={sortByOptions}
          />
        </div>
        <div className="mt-6">
          {tasks.length === 0 ? (
            <div className="flex items-center justify-center">
              <Loading className="h-10 w-10" />
            </div>
          ) : (
            tasks.map((task, idx) => {
              const skipHandler = task.skipped_by_candidate
                ? () => handleCandidateSkip(task.idx)
                : () => handleCoachSkip(task.idx)
              return (
                <ResourceTile
                  key={idx}
                  {...task}
                  withBorder={idx !== tasks.length - 1}
                  onDelete={skipHandler}
                  added_by_coach={task.added_by_coach}
                >
                  <SkipButton
                    onClick={skipHandler}
                    skippedByCandidate={task.skipped_by_candidate}
                    skippedByCoach={task.skipped_by_coach}
                  />
                </ResourceTile>
              )
            })
          )}
        </div>
        <AddTaskModal />
      </div>
    </div>
  )
}

export default CustomizeActionPlanSection
